import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Image, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientPhotos() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [preview, setPreview] = useState(null);
  const [fileData, setFileData] = useState('');
  const [viewPhoto, setViewPhoto] = useState(null);

  const fetchPhotos = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/client-portal/photos`, { withCredentials: true });
      setPhotos(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPhotos(); }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setFileData(reader.result);
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!fileData) { toast.error('Select a photo'); return; }
    setUploading(true);
    try {
      await axios.post(`${API_URL}/api/client-portal/photos`, {
        title, photo_data: fileData
      }, { withCredentials: true });
      toast.success('Photo uploaded');
      setShowUpload(false); setTitle(''); setFileData(''); setPreview(null);
      fetchPhotos();
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/client-portal/photos/${id}`, { withCredentials: true });
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (err) { toast.error('Delete failed'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-photos-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">My Photos</h1>
            <p className="text-sm text-muted-foreground mt-1">Photo gallery and memories</p>
          </div>
          <Button onClick={() => setShowUpload(!showUpload)} className="btn-primary gap-2" data-testid="upload-photo-btn">
            <Plus className="w-4 h-4" /> Upload Photo
          </Button>
        </div>

        {showUpload && (
          <Card className="glass-card border-border">
            <CardContent className="p-4 space-y-3">
              <Input placeholder="Title (optional)" value={title}
                onChange={e => setTitle(e.target.value)}
                className="bg-muted border-border" data-testid="photo-title-input" />
              <input type="file" accept="image/*" onChange={handleFileChange}
                className="text-sm text-muted-foreground" data-testid="photo-file-input" />
              {preview && (
                <img src={preview} alt="Preview" className="w-32 h-32 object-cover rounded-lg border border-border" />
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => { setShowUpload(false); setPreview(null); }}>Cancel</Button>
                <Button onClick={handleUpload} disabled={uploading} className="btn-primary" data-testid="confirm-upload-btn">
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {photos.length === 0 ? (
          <Card className="glass-card border-border">
            <CardContent className="p-8 text-center">
              <Image className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No photos yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map(photo => (
              <Card key={photo.id} className="glass-card border-border overflow-hidden group cursor-pointer"
                data-testid={`photo-${photo.id}`}>
                <div className="aspect-square relative" onClick={() => setViewPhoto(photo)}>
                  <img src={photo.photo_data || photo.photo_url}
                    alt={photo.title || 'Photo'}
                    className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-end">
                    <div className="p-2 w-full opacity-0 group-hover:opacity-100 transition-opacity flex justify-between items-center">
                      <span className="text-white text-xs truncate">{photo.title || 'Photo'}</span>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                        className="p-1 rounded bg-red-500/80 hover:bg-red-500 text-white"
                        data-testid={`delete-photo-${photo.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Fullscreen viewer */}
        {viewPhoto && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setViewPhoto(null)} data-testid="photo-viewer">
            <button className="absolute top-4 right-4 text-white p-2" onClick={() => setViewPhoto(null)}>
              <X className="w-6 h-6" />
            </button>
            <img src={viewPhoto.photo_data || viewPhoto.photo_url}
              alt={viewPhoto.title || 'Photo'}
              className="max-w-full max-h-full object-contain rounded-lg" />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
